import { ServiceResult, Platform } from '../types';
import { createErrorState } from '../utils/errorBoundary';
import { config } from '../config/environment';

const PLAYLIST_API = config.playlistApi.url;  // Function URL for playlist data (legacy)
const EMOTIFY_API = config.emotifyApi.url;  // API Gateway for all routes

// --- Describe Playlist ---

export interface PlaylistInfo {
  id: string;
  name: string;
  image: string;
  owner: string;
}

export interface TrackInfo {
  id: string;
  name: string;
  artist: string;
}

export interface DescribePlaylistResponse {
  playlist: PlaylistInfo;
  tracks: TrackInfo[];
  count: number;
}

export const describePlaylist = async (
  playlistId: string
): Promise<ServiceResult<DescribePlaylistResponse>> => {
  try {
    const resp = await fetch(`${EMOTIFY_API}describePlaylist/${encodeURIComponent(playlistId)}`);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
      const msg = body.error || `Request failed (${resp.status})`;
      return { loading: false, error: createErrorState('api', resp.status === 400 ? msg : 'Failed to load the playlist. It may be private or unavailable.') };
    }
    const data = await resp.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return { data: parsed, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to reach the service. Please try again.') };
  }
};

export const describeYouTubePlaylist = async (
  playlistId: string
): Promise<ServiceResult<DescribePlaylistResponse>> => {
  try {
    const resp = await fetch(`${EMOTIFY_API}describeYouTubePlaylist/${encodeURIComponent(playlistId)}`);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
      const msg = body.error || `Request failed (${resp.status})`;
      return { loading: false, error: createErrorState('api', resp.status === 400 ? msg : 'Failed to load the playlist. It may be private or unavailable.') };
    }
    const data = await resp.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return { data: parsed, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to reach the service. Please try again.') };
  }
};

export interface AppleMusicPlaylistResponse extends DescribePlaylistResponse {
  /** True when only the first page of the playlist could be read */
  truncated?: boolean;
}

export const describeAppleMusicPlaylist = async (
  playlistId: string,
  storefront: string = 'us'
): Promise<ServiceResult<AppleMusicPlaylistResponse>> => {
  try {
    const resp = await fetch(`${EMOTIFY_API}describeAppleMusicPlaylist/${encodeURIComponent(playlistId)}?storefront=${encodeURIComponent(storefront)}`);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
      const msg = body.error || `Request failed (${resp.status})`;
      return { loading: false, error: createErrorState('api', resp.status === 400 ? msg : 'Failed to load the playlist. It may be private or unavailable.') };
    }
    const data = await resp.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return { data: parsed, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to reach the service. Please try again.') };
  }
};

// --- Audio Features ---

export interface AudioFeature {
  id: string;
  spotify_id: string | null;
  valence: number;
  energy: number;
  danceability?: number;
  acousticness?: number;
  instrumentalness?: number;
  liveness?: number;
  speechiness?: number;
  tempo?: number;
  name?: string;
  artist?: string;
  href?: string;
  popularity?: number;
}

export interface AudioFeaturesResponse {
  audioFeatures: AudioFeature[];
  count: number;
}

export const getAudioFeatures = async (
  spotifyIds: string[]
): Promise<ServiceResult<AudioFeaturesResponse>> => {
  if (!spotifyIds.length) {
    return { loading: false, error: createErrorState('data', 'No track IDs provided') };
  }
  try {
    const BATCH_SIZE = 200;
    const allFeatures: AudioFeature[] = [];

    for (let i = 0; i < spotifyIds.length; i += BATCH_SIZE) {
      const batch = spotifyIds.slice(i, i + BATCH_SIZE);
      const resp = await fetch(`${EMOTIFY_API}audioFeatures?ids=${batch.join(',')}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
        return { loading: false, error: createErrorState('api', body.error || 'Failed to fetch audio features') };
      }
      const data = await resp.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      if (parsed.audioFeatures) {
        allFeatures.push(...parsed.audioFeatures);
      }
    }

    return { data: { audioFeatures: allFeatures, count: allFeatures.length }, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to reach the audio features service.') };
  }
};

// --- Recommendations ---

export interface RecommendationsResponse {
  tracks: TrackDetail[];
  count: number;
}

export const getRecommendations = async (
  seedTrackIds: string[],
  valence: number,
  energy: number,
  size: number = 20,
  platform?: Platform
): Promise<ServiceResult<RecommendationsResponse>> => {
  if (!seedTrackIds.length) {
    return { loading: false, error: createErrorState('data', 'No seed track IDs provided') };
  }
  try {
    const params = new URLSearchParams({
      seeds: seedTrackIds.join(','),
      valence: valence.toString(),
      energy: energy.toString(),
      size: size.toString(),
    });
    if (platform) {
      params.set('platform', platform);
    }
    const resp = await fetch(`${EMOTIFY_API}recommendations?${params}`);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
      return { loading: false, error: createErrorState('api', body.error || 'Failed to get recommendations') };
    }
    const data = await resp.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return { data: parsed, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to reach the recommendations service.') };
  }
};


// --- Track Details (Spotify API) ---

export interface TrackDetail {
  id: string;
  name: string;
  artist: string;
  album: string;
  image: string;
  spotifyUrl: string;
}

export interface TrackDetailsResponse {
  tracks: TrackDetail[];
  count: number;
}

export const getTrackDetails = async (
  spotifyIds: string[]
): Promise<ServiceResult<TrackDetailsResponse>> => {
  if (!spotifyIds.length) {
    return { loading: false, error: createErrorState('data', 'No track IDs provided') };
  }
  try {
    const BATCH_SIZE = 100;
    const allTracks: TrackDetail[] = [];

    for (let i = 0; i < spotifyIds.length; i += BATCH_SIZE) {
      const batch = spotifyIds.slice(i, i + BATCH_SIZE);
      const resp = await fetch(`${EMOTIFY_API}trackDetails?ids=${batch.join(',')}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ error: 'Unknown error' }));
        return { loading: false, error: createErrorState('api', body.error || 'Failed to fetch track details') };
      }
      const data = await resp.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      if (parsed.tracks) {
        allTracks.push(...parsed.tracks);
      }
    }

    return { data: { tracks: allTracks, count: allTracks.length }, loading: false };
  } catch {
    return { loading: false, error: createErrorState('api', 'Unable to fetch track details.') };
  }
};
